import {camelize} from 'humps';

import {UserConfigType} from '../../../../service/user/user-config';
import {UserConfigKeyEnum} from '../../../../service/user/user-config-const';
import {QueryMapType} from '../../../../util/type';
import {ReviewsFilterFieldNameEnum} from '../review-list-page-content/reviews-filter/reviews-filter-type';

import {reviewsFilterStateToUrlObject} from './reviews-state-helper';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from './reviews-state-type';

export type ReviewsSavedFilterPeriodPickerTabType = 'publication' | 'collection';

export type ReviewsSavedFilterQueryType = Partial<
    Record<ReviewsFilterFieldNameEnum | ReviewsStateUrlQueryNameEnum, string | void>
>;

export type ReviewsSavedFilterUiStateType = {
    periodPickerActiveTab: ReviewsSavedFilterPeriodPickerTabType;
    expandedSections?: Array<string>;
};

export type ReviewsSavedFilterConfigType = Readonly<{
    enabled: boolean;
    query: ReviewsSavedFilterQueryType;
    uiState: ReviewsSavedFilterUiStateType;
}>;

type StoredReviewsSavedFilterQueryValueType = string | number | void;

type StoredReviewsSavedFilterQueryType = Partial<Record<string, StoredReviewsSavedFilterQueryValueType>>;

type StoredReviewsSavedFilterConfigType = Partial<
    Omit<ReviewsSavedFilterConfigType, 'query' | 'uiState'> & {
        query: StoredReviewsSavedFilterQueryType;
        uiState: ReviewsSavedFilterUiStateType;
    }
>;

const defaultSavedFilterUiState: ReviewsSavedFilterUiStateType = {
    periodPickerActiveTab: 'publication',
};

const defaultSavedFilterConfig: ReviewsSavedFilterConfigType = {
    enabled: false,
    query: {},
    uiState: defaultSavedFilterUiState,
};

const reviewsFilterQueryNames = new Set<string>([
    ...Object.values(ReviewsFilterFieldNameEnum),
    ReviewsStateUrlQueryNameEnum.ratingType,
    ReviewsStateUrlQueryNameEnum.companyIds,
    ReviewsStateUrlQueryNameEnum.filterId,
    ReviewsStateUrlQueryNameEnum.sorting,
]);
const reviewsFormFilterQueryNames = new Set<string>(Object.values(ReviewsFilterFieldNameEnum));
const reviewsFilterQueryNameByAlias = new Map<string, string>(
    [...reviewsFilterQueryNames].flatMap((queryName) => [
        [queryName, queryName],
        [camelize(queryName), queryName],
    ])
);

function isStoredReviewsSavedFilterConfig(
    value: UserConfigType[keyof UserConfigType] | undefined
): value is StoredReviewsSavedFilterConfigType {
    return Boolean(value) && typeof value === 'object';
}

function getStringQueryValue(value: StoredReviewsSavedFilterQueryValueType): string | null {
    if (typeof value === 'string') {
        return value.trim() || null;
    }

    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : null;
    }

    return null;
}

function getStringQueryMap(value: StoredReviewsSavedFilterQueryType = {}): ReviewsSavedFilterQueryType {
    return Object.fromEntries(
        Object.entries(value).flatMap(([key, queryValue]) => {
            const queryName = reviewsFilterQueryNameByAlias.get(key);
            const stringValue = getStringQueryValue(queryValue);

            return queryName && stringValue ? [[queryName, stringValue]] : [];
        })
    );
}

function getUiState(
    value: Partial<ReviewsSavedFilterUiStateType> = defaultSavedFilterUiState
): ReviewsSavedFilterUiStateType {
    const periodPickerActiveTabRaw = value.periodPickerActiveTab;
    const periodPickerActiveTab: ReviewsSavedFilterPeriodPickerTabType =
        periodPickerActiveTabRaw === 'collection' || periodPickerActiveTabRaw === 'publication'
            ? periodPickerActiveTabRaw
            : defaultSavedFilterUiState.periodPickerActiveTab;
    const expandedSections = Array.isArray(value.expandedSections)
        ? value.expandedSections.filter((section) => typeof section === 'string')
        : null;
    const uiState: ReviewsSavedFilterUiStateType = {
        periodPickerActiveTab,
    };

    if (expandedSections) {
        uiState.expandedSections = expandedSections;
    }

    return uiState;
}

export function getSavedReviewsFilterConfig(userConfig?: UserConfigType): ReviewsSavedFilterConfigType {
    const value = userConfig?.[UserConfigKeyEnum.reviewsFilter];

    if (!isStoredReviewsSavedFilterConfig(value)) {
        return defaultSavedFilterConfig;
    }

    return {
        enabled: value.enabled === true,
        query: getStringQueryMap(value.query),
        uiState: getUiState(value.uiState),
    };
}

export function getUserConfigWithSavedReviewsFilter(
    userConfig: UserConfigType | undefined,
    savedFilterConfig: ReviewsSavedFilterConfigType
): UserConfigType {
    return {
        ...userConfig,
        [UserConfigKeyEnum.reviewsFilter]: savedFilterConfig,
    };
}

export function getSavedReviewsFilterQueryFromState(reviewsState: ReviewsStateType): ReviewsSavedFilterQueryType {
    return getStringQueryMap({
        ...reviewsFilterStateToUrlObject(reviewsState.filterState),
        [ReviewsStateUrlQueryNameEnum.ratingType]: reviewsState[ReviewsStateUrlQueryNameEnum.ratingType],
        [ReviewsStateUrlQueryNameEnum.companyIds]: reviewsState[ReviewsStateUrlQueryNameEnum.companyIds],
        [ReviewsStateUrlQueryNameEnum.filterId]: reviewsState.filterId,
        [ReviewsStateUrlQueryNameEnum.sorting]: reviewsState[ReviewsStateUrlQueryNameEnum.sorting],
    });
}

export function hasReviewsFilterQuery(urlQueries: QueryMapType): boolean {
    return Object.keys(urlQueries).some((key) => {
        const value = urlQueries[key];

        return reviewsFormFilterQueryNames.has(key) && typeof value === 'string' && value.trim() !== '';
    });
}
